import './tokens-extra.css';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { TIER_OUTLINE, TIER_SHADOW, type TierId, type TierShape, tierById, UNRATED_FILL } from '#shared/core/ratings';

/**
 * The six tier shapes from shared/src/core/ratings.ts (circle, square, shield, hexagon,
 * diamond, 8-point star), drawn flat with an ink outline and a hard grape shadow. Myth swaps
 * the shadow for the stacked sky, mint, butter, peach copies. Unrated is a dashed frame on
 * lilac pale with no shape. The shape is a cue on its own, so color is never the only one.
 *
 * Three ways in:
 *   <TierFrame tier="starter" size={56}><b>61</b></TierFrame>       HTML, children centered
 *   <svg><TierShapeSvg tier="myth" x={20} y={20} size={80} /></svg>  inside an SVG (PlayerCard)
 *   tierShapePath('hexagon', 0, 0, 100)                              just the path data
 */

/** SVG path data for a tier shape filling the square (x, y, s). */
export function tierShapePath(shape: TierShape, x: number, y: number, s: number): string {
  const f = (n: number) => Math.round(n * 100) / 100;
  const pts = (list: [number, number][]) =>
    `M${list.map(([px, py]) => `${f(x + px * s)},${f(y + py * s)}`).join('L')}Z`;
  switch (shape) {
    case 'circle': {
      const r = s / 2;
      return `M${f(x)},${f(y + r)}a${f(r)},${f(r)} 0 1,0 ${f(s)},0a${f(r)},${f(r)} 0 1,0 ${f(-s)},0Z`;
    }
    case 'square': {
      const k = s * 0.08;
      return `M${f(x + k)},${f(y)}H${f(x + s - k)}Q${f(x + s)},${f(y)} ${f(x + s)},${f(y + k)}V${f(y + s - k)}Q${f(x + s)},${f(y + s)} ${f(x + s - k)},${f(y + s)}H${f(x + k)}Q${f(x)},${f(y + s)} ${f(x)},${f(y + s - k)}V${f(y + k)}Q${f(x)},${f(y)} ${f(x + k)},${f(y)}Z`;
    }
    case 'shield':
      return `M${f(x)},${f(y + s * 0.1)}L${f(x + s / 2)},${f(y)}L${f(x + s)},${f(y + s * 0.1)}V${f(y + s * 0.52)}Q${f(x + s)},${f(y + s * 0.84)} ${f(x + s / 2)},${f(y + s)}Q${f(x)},${f(y + s * 0.84)} ${f(x)},${f(y + s * 0.52)}Z`;
    case 'hexagon':
      return pts([
        [0.25, 0.04],
        [0.75, 0.04],
        [1, 0.5],
        [0.75, 0.96],
        [0.25, 0.96],
        [0, 0.5],
      ]);
    case 'diamond':
      return pts([
        [0.5, 0],
        [1, 0.5],
        [0.5, 1],
        [0, 0.5],
      ]);
    case 'star8': {
      const list: [number, number][] = [];
      for (let i = 0; i < 16; i++) {
        const r = i % 2 === 0 ? 0.5 : 0.4;
        const a = (Math.PI / 8) * i - Math.PI / 2;
        list.push([0.5 + r * Math.cos(a), 0.5 + r * Math.sin(a)]);
      }
      return pts(list);
    }
  }
}

/** Where the content sits inside the shape box, as a fraction of its height (the shield is top heavy). */
export const TIER_CONTENT_Y: Record<TierShape | 'unrated', number> = {
  circle: 0.5,
  square: 0.5,
  shield: 0.44,
  hexagon: 0.5,
  diamond: 0.5,
  star8: 0.5,
  unrated: 0.5,
};

export interface TierMetrics {
  stroke: number;
  shadow: number;
  stackStep: number;
  /** Extra room right and below for the shadow or the stack. */
  pad: number;
}

export function tierMetrics(tier: TierId | null, size: number): TierMetrics {
  const big = size >= 64;
  const stroke = big ? 4 : 3;
  const shadow = big ? 6 : 4;
  const stackStep = big ? 4 : 3;
  const pad = tier === null ? 0 : tier === 'myth' ? stackStep * 4 : shadow;
  return { stroke, shadow, stackStep, pad };
}

export interface TierShapeSvgProps {
  tier: TierId | null;
  x?: number;
  y?: number;
  size: number;
  /** Override the outline width (the 500x700 card wants a thicker one). */
  strokeWidth?: number;
  shadowOffset?: number;
  stackStep?: number;
  /**
   * Shadow fill. Default follows the theme (grape on lilac, ink in Sun mode) through a CSS var;
   * pass a hex (TIER_SHADOW) when the SVG is rasterized for the PNG export, where vars do not exist.
   */
  shadowColor?: string;
  /** Myth stack breathes (static under reduced motion via CSS). */
  animate?: boolean;
}

/** The shape as an SVG <g>, for use inside another SVG. */
export function TierShapeSvg({
  tier,
  x = 0,
  y = 0,
  size,
  strokeWidth,
  shadowOffset,
  stackStep,
  shadowColor = `var(--color-shade, ${TIER_SHADOW})`,
  animate = true,
}: TierShapeSvgProps) {
  const m = tierMetrics(tier, size);
  const sw = strokeWidth ?? m.stroke;
  const inset = sw / 2;
  const bx = x + inset;
  const by = y + inset;
  const bs = size - sw;
  if (tier === null) {
    return (
      <g>
        <rect
          x={bx}
          y={by}
          width={bs}
          height={bs}
          rx={Math.min(6, bs * 0.1)}
          fill={UNRATED_FILL}
          stroke={TIER_OUTLINE}
          strokeWidth={sw}
          strokeDasharray={`${sw * 2} ${sw * 1.4}`}
        />
      </g>
    );
  }
  const t = tierById(tier);
  const d = (dx: number) => tierShapePath(t.shape, bx + dx, by + dx, bs);
  const stack = 'stack' in t ? t.stack : null;
  const step = stackStep ?? m.stackStep;
  const shadow = shadowOffset ?? m.shadow;
  return (
    <g>
      {stack ? (
        <g className={animate ? 'tts-ui-breathe' : undefined}>
          {[...stack].reverse().map((fill, i) => {
            const dx = step * (stack.length - i);
            return <path key={fill} d={d(dx)} fill={fill} stroke={TIER_OUTLINE} strokeWidth={sw * 0.75} />;
          })}
        </g>
      ) : (
        // A var() only works as a style property, not as the fill attribute.
        <path d={d(shadow)} style={{ fill: shadowColor }} />
      )}
      <path d={d(0)} fill={t.fill} stroke={TIER_OUTLINE} strokeWidth={sw} strokeLinejoin="round" />
    </g>
  );
}

export interface TierFrameProps {
  tier: TierId | null;
  /** The shape's side in px (the shadow or stack adds a few px right and below). */
  size?: number;
  children?: ReactNode;
  /** Accessible name; without it the frame is decorative and the caller labels the group. */
  label?: string;
  animate?: boolean;
  className?: string;
}

/** A tier shape with HTML content centered on it (numbers, icons). */
export function TierFrame({ tier, size = 56, children, label, animate = true, className }: TierFrameProps) {
  const { pad } = tierMetrics(tier, size);
  const box = size + pad;
  const shape = tier === null ? 'unrated' : tierById(tier).shape;
  return (
    <span
      {...(label ? { role: 'img' as const, 'aria-label': label } : {})}
      className={clsx('relative inline-block shrink-0 align-middle', className)}
      style={{ width: box, height: box }}
    >
      <svg
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        aria-hidden="true"
        focusable="false"
        className="absolute inset-0 overflow-visible"
      >
        <TierShapeSvg tier={tier} size={size} animate={animate} />
      </svg>
      {children !== undefined && (
        <span
          aria-hidden={label ? true : undefined}
          className="absolute left-0 flex w-full items-center justify-center text-ink"
          style={{ width: size, height: size, top: size * (TIER_CONTENT_Y[shape] - 0.5) }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
